import { ReactNode, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface AdminLayoutProps {
  children: ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const menuItems = [
    {
      path: '/admin',
      label: 'Dashboard',
      icon: 'ri-dashboard-line',
    },
    {
      path: '/admin/alunos',
      label: 'Alunos',
      icon: 'ri-user-line',
    },
    {
      path: '/admin/cursos',
      label: 'Cursos',
      icon: 'ri-book-line',
    },
    {
      path: '/admin/turmas',
      label: 'Turmas',
      icon: 'ri-group-line',
    },
    {
      path: '/admin/matriculas',
      label: 'Matrículas',
      icon: 'ri-file-list-line',
    },
    {
      path: '/admin/configuracoes',
      label: 'Configurações',
      icon: 'ri-settings-3-line',
    },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login-admin');
    } catch (error) {
      console.error('Logout failed:', error);
      // Optional: show user-friendly feedback here
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E1A]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0F1420] border-r border-white/[0.07] z-40">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/[0.07]">
            <Link to="/admin" className="flex items-center gap-3">
              <img
                src="https://static.readdy.ai/image/1cd2ecc8cebcbf00ba1926a19a1b281b/0658f006ef9b7192bc0d5e3622251d68.png"
                alt="INSOL"
                className="h-8 w-auto"
              />
            </Link>
          </div>

          {/* Menu */}
          <nav className="flex-1 p-4 space-y-1">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 text-cyan-400 border border-cyan-500/20'
                      : 'text-white/60 hover:text-white hover:bg-white/[0.03]'
                  }`}
                >
                  <i className={`${item.icon} text-lg`}></i>
                  <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-white/[0.07]">
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/[0.03] transition-all cursor-pointer"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
                  <span className="text-sm font-semibold text-white">
                    {user?.name?.charAt(0) || 'A'}
                  </span>
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white">{user?.name || 'Admin'}</p>
                  <p className="text-xs text-white/40">Administrador</p>
                </div>
                <i className="ri-more-2-fill text-white/40"></i>
              </button>

              {showUserMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowUserMenu(false)}
                  ></div>
                  <div className="absolute bottom-full left-0 right-0 mb-2 bg-[#0F1420] border border-white/[0.07] rounded-lg overflow-hidden z-50">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/60 hover:text-white hover:bg-white/[0.03] transition-all cursor-pointer"
                    >
                      <i className="ri-logout-box-line"></i>
                      <span className="whitespace-nowrap">Sair</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 min-h-screen">{children}</main>
    </div>
  );
}
